import TeamSelectDeprecated from "@/components/messaging/TeamSelect";
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  PrimaryBlueButton,
  PrimaryGrayButton
} from "@velaro/velaro-component-lib";
import React, { useEffect, useState } from "react";
import { IvrIntegrationModel, IVRVoiceConfiguration } from "../types";
import { WorkflowListItem } from "../Workflows/types";
import LoadingSpinner from "@/components/LoadingSpinner";
import useApi from "@/hooks/useApi";

interface Props {
  onClose(): void;
  release(): Promise<void>;
  update(integration: IvrIntegrationModel): void;
  integration: IvrIntegrationModel;
  error: string;
}

export default function IvrIntegrationEditModal(props: Props) {
  const [selectedTeam, setSelectedTeam] = useState(props.integration.teamId);
  const [selectedVoiceConfig, setSelectedVoiceConfig] = useState<
    number | undefined
  >(props.integration.ivrVoiceConfigurationId);
  const [selectedWorkflow, setSelectedWorkflow] = useState<number | undefined>(
    props.integration.workflowId
  );
  const [voiceConfigurations, setVoiceConfigurations] = useState<
    IVRVoiceConfiguration[]
  >([]);
  const [workflows, setWorkflows] = useState<WorkflowListItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [success, setSuccess] = useState<boolean | undefined>(undefined);
  const api = useApi();

  useEffect(() => {
    fetchVoiceConfigurations();
    fetchWorkflows();
  }, []);

  async function fetchVoiceConfigurations() {
    try {
      const response = await api.messaging.get("IVRVoiceConfigurations");
      const data: IVRVoiceConfiguration[] = await response.json();
      setVoiceConfigurations(data);

      // Auto-select first available if none selected
      if (!selectedVoiceConfig && data.length > 0) {
        setSelectedVoiceConfig(data[0].id);
      }
    } catch (error) {
      console.error("Error fetching voice configurations:", error);
    }
  }

  async function fetchWorkflows() {
    try {
      const response = await api.messaging.get("Workflows/List");
      const data: WorkflowListItem[] = await response.json();
      setWorkflows(data);
    } catch (error) {
      console.error("Error fetching workflows:", error);
    }
  }

  async function handleRelease() {
    setLoading(true);
    await props.release();
    setLoading(false);
  }

  return (
    <Modal show={true} maxWidth="max-w-[900px]">
      <ModalHeader title="Edit Phone Number" onClose={() => props.onClose()} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          props.update({
            ...props.integration,
            teamId: selectedTeam,
            ivrVoiceConfigurationId: selectedVoiceConfig,
            workflowId: selectedWorkflow
          });
        }}
      >
        <ModalBody>
          <div className="p-6 max-h-[70vh] overflow-auto">
            <div className="flex mb-4">
              <div className="w-1/3 font-semibold">Team</div>
              <TeamSelectDeprecated
                id="ar-team"
                className="input w-2/3"
                onChange={(e) => setSelectedTeam(e.currentTarget.value)}
                value={selectedTeam}
              />
            </div>
            <div className="flex mb-4">
              <div className="w-1/3 font-semibold">Voice Configuration</div>
              {voiceConfigurations.length === 0 ? (
                <div className="w-2/3 text-gray-500">
                  No voice configurations available. Please create one first.
                </div>
              ) : (
                <select
                  id="voice-config"
                  className="input w-2/3"
                  onChange={(e) =>
                    setSelectedVoiceConfig(
                      e.currentTarget.value
                        ? Number(e.currentTarget.value)
                        : undefined
                    )
                  }
                  value={selectedVoiceConfig || ""}
                >
                  <option value="">Select Voice Configuration</option>
                  {voiceConfigurations.map((config) => (
                    <option key={config.id} value={config.id}>
                      {config.name}
                    </option>
                  ))}
                </select>
              )}
            </div>
            <div className="flex mb-4">
              <div className="w-1/3 font-semibold">Workflow</div>
              <select
                id="workflow"
                className="input w-2/3"
                onChange={(e) =>
                  setSelectedWorkflow(
                    e.currentTarget.value
                      ? Number(e.currentTarget.value)
                      : undefined
                  )
                }
                value={selectedWorkflow || ""}
              >
                <option value="">Auto-detect (dynamic)</option>
                {workflows.map((workflow) => (
                  <option key={workflow.id} value={workflow.id}>
                    {workflow.name}
                  </option>
                ))}
              </select>
            </div>
            <div className="text-crimson-red-500 text-sm mt-2">
              {props.error}
            </div>
          </div>
        </ModalBody>
        <ModalFooter>
          <div className="p-4 flex flex-col mt-4">
            {success === false && (
              <div className="flex flex-row mb-4">
                There was an error saving your changes. Please see Integration
                Log for more details.
              </div>
            )}
            {loading ? (
              <div className="ml-auto flex">
                Saving Changes...
                <LoadingSpinner />
              </div>
            ) : (
              <div className="ml-auto flex flex-row">
                <PrimaryBlueButton
                  type="submit"
                  className="bg-cornflower-blue-500 hover:bg-cornflower-blue-700 text-white py-2 px-4 rounded-full text-sm font-medium"
                  disabled={loading}
                  label="Save"
                />

                <button
                  type="button"
                  className="btn ml-2 bg-red-500 hover:bg-red-600 text-white py-2 px-4 rounded-md text-sm font-medium"
                  disabled={loading}
                  onClick={() => handleRelease()}
                >
                  {loading ? "Releasing..." : "Release Phone Number"}
                </button>
              </div>
            )}
          </div>
        </ModalFooter>
      </form>
    </Modal>
  );
}
